/**
 * Binom save path for useFlowDraft.
 *
 * Issues a single PUT /binom/flows/{id} with the full Flow body. The backend
 * does the 2-request rotation sequence (GET → merge → PUT) and preserves every
 * other rotation field byte-for-byte.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { useUpdateBinomFlow } from '@/hooks';

import type { DraftPath } from '../utils/flowDraft.utils';

export interface PathGroupRaw {
  id?: string;
  name?: string;
  active?: boolean;
  smartRotation?: boolean;
  paths?: Array<{
    id?: string;
    name?: string;
    weight: number;
    active?: boolean;
    landers?: Array<{ id: string; name: string; weight: number }>;
    offers?: Array<{ id: string; name: string; weight: number }>;
  }>;
}

export interface SaveBinomFullFlowParams {
  flowId: string;
  flowName: string;
  draftPaths: DraftPath[];
  pathGroupsRaw?: PathGroupRaw[];
  mutation: ReturnType<typeof useUpdateBinomFlow>;
}

export async function saveBinomFullFlow(
  p: SaveBinomFullFlowParams,
): Promise<void> {
  const defaultPaths = p.draftPaths
    .filter((d) => !d.groupId || d.groupId === 'default')
    .map(toFlowPath);
  const groupedDrafts = p.draftPaths.filter(
    (d) => d.groupId && d.groupId !== 'default',
  );
  const pathGroups = (p.pathGroupsRaw ?? []).map((g) => ({
    id: String(g.id ?? ''),
    name: g.name ?? '',
    active: Boolean(g.active),
    smartRotation: Boolean(g.smartRotation),
    paths: groupedDrafts.filter((d) => d.groupId === g.id).map(toFlowPath),
  }));
  await p.mutation.mutateAsync({
    id: p.flowId,
    name: p.flowName,
    isDefault: true,
    defaultPaths,
    ...(pathGroups.length > 0 ? { pathGroups } : {}),
  });
}

function toFlowPath(d: DraftPath) {
  return {
    id: d.id,
    name: d.name,
    weight: d.weight,
    active: d.active,
    landers: d.landers.map((l) => ({
      id: l.id,
      name: l.name,
      weight: l.weight,
    })),
    offers: d.offers.map((o) => ({
      id: o.id,
      name: o.name,
      weight: o.weight,
    })),
  };
}
